import type { ComponentType, CSSProperties, ReactNode } from 'react';
import { WRAP, Btn, ArrowIcon, Eyebrow } from '@/components/home-v2/ui';

/**
 * Building blocks for content pages (about, policies, guides, contact…) in the home v2 style:
 * warm paper background, Lexend, mono uppercase eyebrows, white cards with hairline borders,
 * orange/yellow accents. Server-safe (no hooks), so client pages can use them too.
 */

export { WRAP, Btn, ArrowIcon, Eyebrow };

type BtnKind = 'dark' | 'orange' | 'line' | 'white' | 'yellow';
const BTN: Record<BtnKind, string> = {
  dark: 'bg-hv-ink text-white hover:bg-black',
  orange: 'bg-hv-orange text-white hover:brightness-95',
  line: 'border-[1.5px] border-hv-ink text-hv-ink hover:bg-hv-ink hover:text-white',
  white: 'bg-white text-hv-ink hover:bg-hv-paper',
  yellow: 'bg-hv-yellow text-hv-ink hover:brightness-95',
};

/** Classes for a <button> that looks like Btn (for forms and click handlers). */
export function btnClass(kind: BtnKind = 'dark', extra = '') {
  return `inline-flex min-h-12 max-w-full items-center justify-center gap-2.5 rounded-xl px-6 py-2 text-center text-[15px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${BTN[kind]} ${extra}`;
}

/** Text input / select / textarea styling. */
export const inputClass =
  'w-full rounded-xl border border-hv-line bg-white px-4 py-3 text-[15px] text-hv-ink placeholder:text-hv-soft outline-none transition focus:border-hv-ink focus:ring-2 focus:ring-hv-yellow/40';

/** Page shell: theme tokens and fonts for everything inside. */
export function SitePage({ children, className = '', style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return <div className={`hv-page min-h-screen bg-hv-paper font-lexend text-hv-ink ${className}`} style={style}>{children}</div>;
}

/** Page header: eyebrow, the page's only h1, intro text, optional meta line, actions and a right-hand aside. */
export function PageHero({ eyebrow, title, description, meta, actions, aside, children }: {
  eyebrow?: ReactNode; title: ReactNode; description?: ReactNode; meta?: ReactNode;
  actions?: ReactNode; aside?: ReactNode; children?: ReactNode;
}) {
  return (
    <section className="border-b border-hv-line bg-hv-paper">
      <div className={`${WRAP} grid gap-10 py-12 md:py-20 ${aside ? 'lg:grid-cols-[1.1fr_1fr] lg:items-center' : ''}`}>
        <div className="max-w-[760px]">
          {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
          <h1 className="text-[36px] leading-[1.04] tracking-[-0.025em] md:text-[56px]">{title}</h1>
          {description && <p className="mt-5 max-w-[620px] text-[17px] leading-relaxed text-hv-soft md:text-lg">{description}</p>}
          {meta && <p className="mt-4 font-hv-mono text-[12px] uppercase tracking-[0.04em] text-hv-soft">{meta}</p>}
          {actions && <div className="mt-8 flex flex-wrap gap-3">{actions}</div>}
          {children}
        </div>
        {aside && <div>{aside}</div>}
      </div>
    </section>
  );
}

/** Highlighted word inside a PageHero/Section title. */
export function Mark({ children }: { children: ReactNode }) {
  return <span className="text-hv-orange">{children}</span>;
}

const TONE = {
  paper: 'bg-hv-paper text-hv-ink',
  white: 'bg-white text-hv-ink',
  card: 'bg-hv-card text-hv-ink',
  dark: 'bg-hv-ink text-white',
} as const;

/** Page section with an optional eyebrow / h2 / subtitle heading. */
export function Section({ id, eyebrow, title, subtitle, right, tone = 'paper', className = '', children }: {
  id?: string; eyebrow?: ReactNode; title?: ReactNode; subtitle?: ReactNode; right?: ReactNode;
  tone?: keyof typeof TONE; className?: string; children?: ReactNode;
}) {
  const sub = tone === 'dark' ? 'text-white/70' : 'text-hv-soft';
  return (
    <section id={id} className={`${TONE[tone]} ${className}`}>
      <div className={`${WRAP} py-12 md:py-16`}>
        {(eyebrow || title || subtitle || right) && (
          <div className="mb-8 flex flex-col gap-4 md:mb-10 md:flex-row md:items-end md:justify-between">
            <div>
              {eyebrow && <Eyebrow className={`mb-3 ${tone === 'dark' ? '!text-hv-yellow' : ''}`}>{eyebrow}</Eyebrow>}
              {title && <h2 className="text-[28px] leading-[1.08] tracking-[-0.02em] md:text-[40px]">{title}</h2>}
              {subtitle && <p className={`mt-3 max-w-[640px] text-base leading-relaxed ${sub}`}>{subtitle}</p>}
            </div>
            {right}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

/** White surface with a hairline border. */
export function Card({ children, className = '', as: Tag = 'div' }: {
  children: ReactNode; className?: string; as?: 'div' | 'article' | 'section' | 'li';
}) {
  return <Tag className={`rounded-2xl border border-hv-line bg-white p-6 md:p-8 ${className}`}>{children}</Tag>;
}

/** Small icon tile (lucide icons or any component taking className). */
export function IconBadge({ icon: Icon, tone = 'card' }: {
  icon: ComponentType<{ className?: string; strokeWidth?: number }>; tone?: 'card' | 'yellow' | 'dark';
}) {
  const cls = tone === 'yellow' ? 'bg-hv-yellow text-hv-ink' : tone === 'dark' ? 'bg-hv-ink text-hv-yellow' : 'bg-hv-card text-hv-orange';
  return (
    <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${cls}`}>
      <Icon className="h-5 w-5" strokeWidth={1.8} />
    </div>
  );
}

/**
 * Renders policy text from parsed-policies.json: paragraphs separated by blank lines,
 * `| a | b |` tables (with a --- separator row), `- ` bullet lists and "Please note:" callouts.
 */
export function PolicyText({ text }: { text: string }) {
  return (
    <div className="space-y-4 text-[15px] leading-[1.8] text-hv-soft">
      {text.split('\n\n').map((para, i) => {
        if (para.includes('|') && para.includes('---')) {
          const rows = para.split('\n').filter((l) => l.trim() && !l.includes('---'));
          const cells = (row: string) => row.split('|').filter((c) => c.trim()).map((c) => c.trim());
          return (
            <div key={i} className="overflow-x-auto rounded-xl border border-hv-line">
              <table className="min-w-full text-sm">
                <thead className="bg-hv-card">
                  <tr>{cells(rows[0]).map((c, k) => (
                    <th key={k} className="px-4 py-3 text-left font-hv-mono text-[11px] font-medium uppercase tracking-[0.04em] text-hv-ink">{c}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {rows.slice(1).map((row, k) => (
                    <tr key={k} className="border-t border-hv-line bg-white">
                      {cells(row).map((c, l) => <td key={l} className="px-4 py-3 text-hv-ink/80">{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (para.includes('- ')) {
          return (
            <ul key={i} className="space-y-2.5">
              {para.split('\n').filter((l) => l.trim()).map((item, j) => (
                <li key={j} className="flex items-start gap-3">
                  <span className="mt-[11px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-hv-orange" />
                  <span>{item.replace(/^-\s*/, '')}</span>
                </li>
              ))}
            </ul>
          );
        }
        if (para.startsWith('Please note:')) {
          return (
            <p key={i} className="rounded-xl border border-hv-yellow/50 bg-hv-yellow/10 px-5 py-4 text-[14px] text-hv-ink">{para}</p>
          );
        }
        return <p key={i}>{para}</p>;
      })}
    </div>
  );
}
